import React, { useRef, useState } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Handle, Position } from "@xyflow/react";
import { CircuitBoard } from "lucide-react";
import { icon, label } from "../../../../config";
import SwitchNodePopup from "../SwitchNodePopup";
import NodeDeleteConfirmPopup from "./NodeDeleteConfirmPopup";
import "./SwitchNode.scss";

const SwitchNode = ({ data, id }) => {
  const [showExpressionPopup, setShowExpressionPopup] = useState(false);
  const [showDeleteConfirmationPopup, setShowDeleteConfirmationPopup] = useState(false);
  const deleteIconRef = useRef(null);
  const deleteRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  /**
   * Opens the delete confirmation popup
   * @param {Object} e - Event object
   */
  const handleDelete = (e) => {
    e.stopPropagation();
    setShowDeleteConfirmationPopup(true);
  };

  /**
   * Confirms deletion — calls the actual delete handler
   */
  const handleDeleteAction = () => {
    setShowDeleteConfirmationPopup(false);
    if (data.onDeleteNode) {
      data.onDeleteNode(id, "switchNode");
    }
  };

  /**
   * Dismisses the popup without deleting
   */
  const hideOnBlur = () => {
    setShowDeleteConfirmationPopup(false);
  };

  /**
   * Handles edit node action - opens expression builder popup
   * @param {Object} e - Event object
   * @author Abhishek Dhillon
   */
  const handleEdit = (e) => {
    e.stopPropagation();
    setShowExpressionPopup(true);
  };

  /**
   * Handles closing the expression builder popup
   * @author Abhishek Dhillon
   */
  const handleClosePopup = () => {
    setShowExpressionPopup(false);
  };

  return (
    <div className={`switch-node ${data?.isCompleted ? "node-completed" : ""}`}>
      <Handle type='target' position={Position.Top} />
      <div className='node-header'>
        <div className='node-icon'>
          <CircuitBoard className='lucid-icon-common' />
        </div>
        <span className='node-title'>{data.label}</span>

        {!data.isReadOnly && !data.isCompleted && (
          <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
            <div className='header-actions d-flex align-items-center gap-2'>
              <span className='assignment-display-edit-icon' onClick={handleEdit}>
                {icon.EDIT_ICON}
              </span>
              <span ref={deleteIconRef} className={`assignment-display-delete-icon ${showDeleteConfirmationPopup ? "icon-active" : ""}`} onClick={handleDelete} title='Delete'>
                {icon.DELETE_ICON}
              </span>
            </div>
          </Tooltip>
        )}
      </div>
      <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
        <div title='Event trigger on false'>
          <Handle type='source' position={Position.Bottom} id='negative' style={{ left: "30%" }} className='negative-handle' />
        </div>
      </Tooltip>
      <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
        <div title='Event trigger on true'>
          <Handle type='source' position={Position.Bottom} id='positive' style={{ left: "70%" }} className='positive-handle' />
        </div>
      </Tooltip>

      {/* Expression Builder Popup */}
      {showExpressionPopup && (
        <Dialog show={showExpressionPopup} onClose={handleClosePopup}>
          <SwitchNodePopup onClose={handleClosePopup} nodeId={id} />
        </Dialog>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirmationPopup && (
        <NodeDeleteConfirmPopup
          anchor={deleteIconRef.current}
          deleteRef={deleteRef}
          blurTimeoutRef={blurTimeoutRef}
          onConfirm={handleDeleteAction}
          onCancel={hideOnBlur}
          hideOnBlur={hideOnBlur}
        />
      )}
    </div>
  );
};

export default SwitchNode;
